import { API_URL } from 'config';
import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../../context/LanguageContext';

export const NewsTab = () => {
  const { t } = useLanguage();
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchNews = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${API_URL}/news/get-update`, {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache'
          }
        });

        if (!response.ok) {
          throw new Error('Failed to fetch news');
        }

        const data = await response.json();
        
        // Проверяем, что data это массив
        if (Array.isArray(data)) {
          const sortedNews = data.sort((a, b) => {
            return new Date(b.date) - new Date(a.date);
          });
          setNews(sortedNews);
        } else {
          setNews([]);
        }
        setError(null);
      } catch (err) {
        console.error('Error fetching news:', err);
        setError(t('news.error'));
      } finally {
        setLoading(false);
      }
    };

    fetchNews();
  }, [t]);

  const parseMarkdown = (text) => {
    if (!text) return null;
    
    // Разбиваем на строки и обрабатываем
    const lines = text.split('\n');
    const result = [];
    let i = 0;
    
    while (i < lines.length) {
      const line = lines[i];
      const trimmed = line.trim();
      
      // Пропускаем пустые строки
      if (trimmed.length === 0) {
        i++;
        continue;
      }
      
      // Заголовки
      if (trimmed.startsWith('### ')) {
        result.push(<h3 key={i} className="news-h3">{parseInlineMarkdown(trimmed.replace('###', '').trim())}</h3>);
        i++;
        continue;
      }
      if (trimmed.startsWith('## ')) {
        result.push(<h2 key={i} className="news-h2">{parseInlineMarkdown(trimmed.replace('##', '').trim())}</h2>);
        i++;
        continue;
      }
      if (trimmed.startsWith('# ')) {
        result.push(<h1 key={i} className="news-h1">{parseInlineMarkdown(trimmed.replace('#', '').trim())}</h1>);
        i++;
        continue;
      }
      
      // Списки
      if (trimmed.startsWith('* ') || trimmed.startsWith('- ')) {
        const listItems = [];
        while (i < lines.length && (lines[i].trim().startsWith('* ') || lines[i].trim().startsWith('- '))) {
          let content = lines[i].trim().replace(/^[*-]\s*/, '');
          listItems.push(<li key={i}>{parseInlineMarkdown(content)}</li>);
          i++;
        }
        result.push(<ul key={`list-${i}`} className="news-list">{listItems}</ul>);
        continue;
      }
      
      // Цитаты
      if (trimmed.startsWith('> ')) {
        result.push(
          <blockquote key={i} className="news-blockquote">
            {parseInlineMarkdown(trimmed.replace('>', '').trim())}
          </blockquote>
        );
        i++;
        continue;
      }
      
      // Обычный параграф
      result.push(<p key={i} className="news-text">{parseInlineMarkdown(trimmed)}</p>);
      i++;
    }
    
    return result;
  };

  const parseInlineMarkdown = (text) => {
    if (!text) return text;
    
    // Ищем **...**
    const parts = [];
    const boldRegex = /\*\*(.*?)\*\*/g;
    let match;
    let index = 0;
    let lastIndex = 0;
    
    while ((match = boldRegex.exec(text)) !== null) {
      // Добавляем текст до матча
      if (match.index > lastIndex) {
        parts.push(text.substring(lastIndex, match.index));
      }
      // Добавляем жирный текст
      parts.push(<strong key={`bold-${index}`}>{match[1]}</strong>);
      lastIndex = match.index + match[0].length;
      index++;
    }
    
    // Добавляем оставшийся текст
    if (lastIndex < text.length) {
      const remaining = text.substring(lastIndex);
      // Обрабатываем остаток на наличие других маркдаун элементов
      parts.push(parseSimpleMarkdown(remaining));
    }
    
    return parts.length > 0 ? parts : parseSimpleMarkdown(text);
  };

  const parseSimpleMarkdown = (text) => {
    if (!text) return text;
    
    // Обработка жирного текста **текст** (запасной вариант)
    if (text.includes('**')) {
      return text.split('**').map((part, idx) => {
        if (idx % 2 === 1) {
          return <strong key={idx}>{part}</strong>;
        }
        return part;
      });
    }
    
    // Обработка кода `текст`
    if (text.includes('`')) {
      const parts = text.split('`');
      return parts.map((part, idx) => {
        if (idx % 2 === 1) {
          return <code key={idx}>{part}</code>;
        }
        return part;
      });
    }
    
    // Обработка ссылок [текст](url)
    const linkRegex = /\[(.*?)\]\((.*?)\)/g;
    if (linkRegex.test(text)) {
      const result = [];
      let lastIndex = 0;
      let match;
      linkRegex.lastIndex = 0;
      
      while ((match = linkRegex.exec(text)) !== null) {
        if (match.index > lastIndex) {
          result.push(text.substring(lastIndex, match.index));
        }
        result.push(
          <a key={`link-${match.index}`} href={match[2]} target="_blank" rel="noopener noreferrer" className="news-link">
            {match[1]}
          </a>
        );
        lastIndex = match.index + match[0].length;
      }
      
      if (lastIndex < text.length) {
        result.push(text.substring(lastIndex));
      }
      
      return result;
    }
    
    // Обработка смайлов и эмодзи (просто возвращаем текст)
    return text;
  };

  const formatDate = (dateString) => {
    if (!dateString || dateString === '') {
      return t('news.latestUpdate');
    }
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return t('news.latestUpdate');
      }
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      }).toUpperCase();
    } catch (e) {
      return t('news.latestUpdate');
    }
  };

  if (loading) {
    return (
      <div>
        <div className="dash-title-block">
          <h2 className="dash-title">{t('dashboard.newsTitle')}</h2>
        </div>
        <div className="news-container">
          <div className="news-loading">
            <i className="fas fa-spinner fa-spin"></i> {t('news.loading')}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <div className="dash-title-block">
          <h2 className="dash-title">{t('dashboard.newsTitle')}</h2>
        </div>
        <div className="news-container">
          <div className="news-error">
            <i className="fas fa-exclamation-triangle"></i> {error}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="dash-title-block">
        <h2 className="dash-title">{t('dashboard.newsTitle')}</h2>
      </div>
      <div className="news-container">
        {news.length === 0 ? (
          <div className="news-empty">
            <p>{t('news.noUpdates')}</p>
          </div>
        ) : (
          news.map((item, index) => (
            <div key={index} className="news-card">
              <div className="news-card-meta">
                {formatDate(item.date)} {'//'} {t('news.version')} {item.version || t('news.update')}
              </div>
              <div className="md-content">
                {parseMarkdown(item.content)}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};